import classNames from 'classnames';
import React, { useEffect, useMemo } from 'react';

import Cmip6ExperimentHelp from '../../common/Cmip6ExperimentHelp';
import CheckboxGroup from '../../lib/CheckboxGroup';
import ItemBoxes from '../../lib/ItemBoxes';
import Selects from '../../lib/Selects';
import { PartialTr } from '../../lib/types';
import { useAppDispatch as useDispatch, useAppSelector as useSelector } from '../../store';
import { useGetAvailableVariablesQuery, useGetDatabaseConfigQuery, useGetDatabaseEnsemblesQuery, useGetDatabaseExperimentsQuery, useGetDatabaseFrequenciesQuery, useGetDatabaseModelsQuery, useGetDatabaseVariablesQuery, useGetKwargsQuery } from '../api/apiSlice';
import { selectDatabase, selectEnsemble, selectEnsemble2, selectExperiment, selectFrequency, selectModel, selectVariables, updateDatabase, updateEnsemble, updateEnsemble2, updateExperiment, updateFrequency, updateModel, updateVariables } from './fileRepertoireSlice';
import styles from './MultiFileSelector.scss';

const Variables = () => {
  const dispatch = useDispatch();

  const database = useSelector(selectDatabase);
  const ensemble = useSelector(selectEnsemble);
  const experiment = useSelector(selectExperiment);
  const frequency = useSelector(selectFrequency);
  const model = useSelector(selectModel);
  const variables = useSelector(selectVariables);

  const items = (() => {
    const { data: config } = useGetDatabaseConfigQuery();
    const { data: variables } = useGetDatabaseVariablesQuery();
    const { data: availableVariables } = useGetAvailableVariablesQuery(
      config?.data_complete ? {database, all: true} : {database, model, ensemble, experiment, frequency, all: false}
    );

    const availableVariablesSet = useMemo(() => {
      return new Set(availableVariables);
    }, [availableVariables]);

    const augumenteVariables = useMemo(() => {
      return variables.map(variable => ({...variable, disabled: !availableVariablesSet.has(variable.variable_name)}));
    }, [variables, availableVariablesSet]);
    return augumenteVariables;
  })();

  if (items == null) {
    return null;
  }

  return (
    <CheckboxGroup
      className={classNames(styles.variables, styles.selectVariables)}
      handleChange={variables => dispatch(updateVariables({variables}))}
      items={items}
      labelFormat={'{variable_name} ({variable_longname})'}
      values={variables ?? []}
      valueFormat={'{variable_name}'}
    />
  );
};

interface MultiFileSelectorProps {
  className?: string;
  tr: PartialTr;
}
const MultiFileSelector = ({className, tr}: MultiFileSelectorProps) => {
  const dispatch = useDispatch();

  const { data: config } = useGetDatabaseConfigQuery();
  const { data: ensembles } = useGetDatabaseEnsemblesQuery();
  const { data: experiments } = useGetDatabaseExperimentsQuery();
  const { data: frequencies } = useGetDatabaseFrequenciesQuery();
  const { data: models } = useGetDatabaseModelsQuery();

  const database = useSelector(selectDatabase);
  const ensemble = useSelector(selectEnsemble);
  const ensemble2 = useSelector(selectEnsemble2);
  const experiment = useSelector(selectExperiment);
  const frequency = useSelector(selectFrequency);
  const model = useSelector(selectModel);

  const { data: kwargs } = useGetKwargsQuery(window.location.href);
  useEffect(() => {
    const database = kwargs?.climate_database as string;
    dispatch(updateDatabase({database}));
  }, [kwargs]);

  useEffect(() => {
    if (ensemble == null && ensembles.length > 0) {
      dispatch(updateEnsemble({ensemble: ensembles[0].ensemble_shortname}));
    }
  }, [ensembles]);

  useEffect(() => {
    if (experiment == null && experiments.length > 0) {
      dispatch(updateExperiment({experiment: experiments[0].experiment_shortname}));
    }
  }, [experiments]);

  useEffect(() => {
    if (frequency == null && frequencies.length > 0) {
      dispatch(updateFrequency({frequency: frequencies[0].frequency_shortname}));
    }
  }, [frequencies]);

  useEffect(() => {
    if (model == null && models.length > 0) {
      dispatch(updateModel({model: models[0].model_shortname}));
    }
  }, [models]);

  const ensemble1Index = ensemble ? ensembles.map(ensemble => ensemble.ensemble_shortname).indexOf(ensemble) : -1;
  const trailingEnsembles = useMemo(() => {
    return ensembles.filter((_, i) => i >= ensemble1Index);
  }, [ensembles, ensemble1Index]);

  useEffect(() => {
    if (trailingEnsembles.length > 0) {
      if (!trailingEnsembles.some(e => e.ensemble_shortname === ensemble2)) {
        dispatch(updateEnsemble2({ensemble2: trailingEnsembles[0].ensemble_shortname}));
      }
    }
  }, [ensemble2, trailingEnsembles]);


  return (
    <div>
      <div className={className ?? styles.basicForm}>
        <div className={styles.model}>
          <div className={styles.itemTitle}>1. {tr('Select dataset [longName/shortName (calendar)]', 'データセット [名称/略称 (カレンダー)]')}</div>
          <Selects
            className={styles.control}
            handleChange={model => dispatch(updateModel({model}))}
            items={models}
            labelFormat={'{model_name}/{model_shortname} ({calendar})'}
            value={model}
            valueFormat={'{model_shortname}'}
          />
        </div>
        <div className={styles.ensemble}>
          <div className={styles.itemTitle}>2. {tr('Select ensemble run', 'アンサンブル')}{database === 'cmip6' && <Cmip6ExperimentHelp tr={tr} />}</div>
          {(() => {
            if (config?.multi_ensembles) {
              return (
                <React.Fragment>
                  <Selects
                    className={styles.control50}
                    handleChange={ensemble => dispatch(updateEnsemble({ensemble}))}
                    items={ensembles}
                    labelFormat={'{ensemble_name}'}
                    value={ensemble}
                    valueFormat={'{ensemble_shortname}'}
                  />
                  <Selects
                    className={styles.control50}
                    handleChange={ensemble2 => dispatch(updateEnsemble2({ensemble2}))}
                    items={trailingEnsembles}
                    labelFormat={'{ensemble_name}'}
                    value={ensemble2}
                    valueFormat={'{ensemble_shortname}'}
                  />
                </React.Fragment>
              );
            } else {
              return (
                <Selects
                  className={styles.control}
                  handleChange={ensemble => dispatch(updateEnsemble({ensemble}))}
                  items={ensembles}
                  labelFormat={'{ensemble_name}'}
                  value={ensemble}
                  valueFormat={'{ensemble_shortname}'}
                />
              );
            }
          })()}
        </div>
        <div className={styles.experiment}>
          <div className={styles.itemTitle}>3. {tr('Select scenario', 'シナリオ')}</div>
          <ItemBoxes
            className={styles.experiments}
            handleChange={experiment => dispatch(updateExperiment({experiment}))}
            items={experiments}
            labelFormat={'{experiment_name}'}
            value={experiment}
            valueFormat={'{experiment_shortname}'}
          />
        </div>
        <div className={styles.frequency}>
          <div className={styles.itemTitle}>4. {tr('Select temporal resolution', '時間解像度')}</div>
          <ItemBoxes
            className={styles.experiments}
            handleChange={frequency => dispatch(updateFrequency({frequency}))}
            items={frequencies}
            labelFormat={'{frequency_name}'}
            value={frequency}
            valueFormat={'{frequency_shortname}'}
          />
        </div>
        <div className={styles.variable}>
          <div className={styles.itemTitle}>5. {tr('Select variables', '変数')}</div>
          <Variables />
        </div>
      </div>
    </div>
  );
};

export default MultiFileSelector;
